import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  FiFolder, FiFolderPlus, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck,
  FiSearch, FiChevronRight, FiAlertCircle, FiInfo, FiMove, FiUsers,
} from 'react-icons/fi';
import { loadTable, addRow, saveRow, isApiReady, checkApi, getTableData, saveTableData, getNextId } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import './ManageFoldersTab.css';

// =============================================
// Types
// =============================================
interface OwnerEntity {
  owner_type: 'account_owner' | 'family_member' | 'business' | 'client_personal' | 'client_business';
  owner_id: number;
  display_name: string;
  badge: string;
  nickname?: string;   // from users.nickname / business acronym — shown for clarity
}

interface FolderRow {
  folder_id: number;
  account_owner_id: number;
  parent_folder_id: number | null;
  folder_name: string;
  folder_path: string;
  folder_depth: number;
  folder_description: string;
  folder_icon: string;
  folder_color: string;
  umbrella_folder: number;
  sort_order: number;
  is_active: number;
  is_deleted: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

interface FolderOwnerRow {
  folder_owner_id: number;
  folder_id: number;
  owner_type: string;
  owner_id: number;
  is_primary: number;
  access_level: string;
  is_active: number;
  is_deleted: number;
  added_by: number;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

interface FolderFormFields {
  folder_name: string;
  folder_description: string;
  folder_icon: string;
  folder_color: string;
}

interface FolderLevel {
  inputText: string;
  selectedFolder: FolderRow | null;
  showAutocomplete: boolean;
}

// =============================================
// Constants
// =============================================
const EMPTY_FORM: FolderFormFields = {
  folder_name: '',
  folder_description: '',
  folder_icon: '',
  folder_color: '',
};

const MAX_SUBFOLDER_LEVELS = 3;

const LEVEL_LABELS = ['Folder Name', 'Sub-folder Name', 'Sub-sub-folder Name', 'Sub-sub-sub-folder Name'];

const FOLDER_ICONS: { key: string; label: string; emoji: string }[] = [
  { key: 'bank', label: 'Bank', emoji: '🏦' },
  { key: 'medical', label: 'Medical', emoji: '🏥' },
  { key: 'tax', label: 'Tax', emoji: '📋' },
  { key: 'receipts', label: 'Receipts', emoji: '🧾' },
  { key: 'legal', label: 'Legal', emoji: '⚖️' },
  { key: 'insurance', label: 'Insurance', emoji: '🛡️' },
  { key: 'personal', label: 'Personal', emoji: '👤' },
  { key: 'business', label: 'Business', emoji: '💼' },
  { key: 'education', label: 'Education', emoji: '🎓' },
  { key: 'property', label: 'Property', emoji: '🏠' },
  { key: 'investment', label: 'Investment', emoji: '📈' },
  { key: 'other', label: 'Other', emoji: '📁' },
];

const FOLDER_COLORS = [
  '#1a237e', '#0d47a1', '#01579b', '#006064',
  '#004d40', '#1b5e20', '#33691e', '#827717',
  '#f57f17', '#e65100', '#bf360c', '#b71c1c',
  '#880e4f', '#4a148c', '#311b92', '#455a64',
];

const NOW_ISO = () => new Date().toISOString().replace('T', ' ').substring(0, 19);

// =============================================
// Helpers
// =============================================

function getCurrentUserFromData(usersData: Record<string, any>[]): { user_id: number; owner_id: number | null; role: string; user_type_id: number; first_name: string; last_name: string } | null {
  try {
    const raw = localStorage.getItem('akt_session_user');
    if (!raw) return null;
    const session = JSON.parse(raw);
    const sessionId = Number(session.id || session.user_id);
    if (!sessionId) return null;
    // eslint-disable-next-line eqeqeq
    const userRow = usersData.find((u: any) => Number(u.user_id) === sessionId);
    if (!userRow) return null;
    return {
      user_id: sessionId,
      owner_id: userRow.owner_id == null || userRow.owner_id === '' ? null : Number(userRow.owner_id),
      role: session.role || '',
      user_type_id: Number(userRow.user_type_id) || 0,
      first_name: userRow.first_name || '',
      last_name: userRow.last_name || '',
    };
  } catch { /* ignore */ }
  return null;
}

function getAccountOwnerFromData(usersData: Record<string, any>[]): Record<string, any> | null {
  return usersData.find((u: any) => u.owner_id == null || u.owner_id === '') || usersData[0] || null;
}

function getAccountOwnerForUserFromData(userId: number, usersData: Record<string, any>[]): Record<string, any> | null {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find((u: any) => u.user_id == userId);
  if (!user) return null;
  if (user.owner_id == null || user.owner_id === '') return user;
  // eslint-disable-next-line eqeqeq
  return usersData.find((u: any) => u.user_id == user.owner_id) || null;
}

// Owner nickname from Profile (users.nickname) / business acronym — shown next to
// the name for quick recognition when an account owner files for family/businesses.
function resolveOwnerNicknameFromData(ownerType: string, ownerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    return b ? String(b.busi_acronym || b.acronym || b.busi_acro || '') : '';
  }
  // eslint-disable-next-line eqeqeq
  const u = usersData.find((u: any) => u.user_id == ownerId);
  return u ? String(u.nickname || '') : '';
}

function getOwnerEntitiesFromData(accountOwnerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): OwnerEntity[] {
  const entities: OwnerEntity[] = [];
  const users = usersData;
  const businesses = businessesData;

  // eslint-disable-next-line eqeqeq
  const owner = users.find((u: any) => u.user_id == accountOwnerId);
  if (owner) {
    entities.push({
      owner_type: 'account_owner',
      owner_id: Number(owner.user_id),
      display_name: `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || 'Account Owner',
      badge: 'Owner',
    });
  }

  users
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted !== 1 && u.relationship !== 'Personal Client')
    .forEach((u: any) => {
      entities.push({
        owner_type: 'family_member',
        owner_id: Number(u.user_id),
        display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Family Member',
        badge: 'Family',
      });
    });

  businesses
    // eslint-disable-next-line eqeqeq
    .filter((b: any) => b.owner_id == accountOwnerId && b.is_deleted != 1 && b.is_client_business != 1)
    .forEach((b: any) => {
      entities.push({
        owner_type: 'business',
        owner_id: Number(b.business_id),
        display_name: b.busi_legal_name || b.busi_operating_name || 'Business',
        badge: 'Business',
      });
    });

  // Personal clients + their family members + their businesses
  const personalClients = users
    // eslint-disable-next-line eqeqeq
    .filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted != 1 && u.relationship === 'Personal Client');

  personalClients.forEach((client: any) => {
    const clientId = Number(client.user_id);
    const clientName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client';

    entities.push({
      owner_type: 'client_personal',
      owner_id: clientId,
      display_name: clientName,
      badge: 'Client',
    });

    // Client's family members
    users
      // eslint-disable-next-line eqeqeq
      .filter((u: any) => u.owner_id == clientId && u.is_deleted != 1 && u.relationship !== 'Personal Client')
      .forEach((u: any) => {
        entities.push({
          owner_type: 'client_personal',
          owner_id: Number(u.user_id),
          display_name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || 'Client Family',
          badge: `${clientName}'s Family`,
        });
      });

    // Client's businesses
    businesses
      // eslint-disable-next-line eqeqeq
      .filter((b: any) => b.owner_id == clientId && b.is_deleted != 1 && b.is_client_business == 1)
      .forEach((b: any) => {
        entities.push({
          owner_type: 'client_business',
          owner_id: Number(b.business_id),
          display_name: b.busi_legal_name || b.busi_operating_name || 'Client Business',
          badge: `${clientName}'s Biz`,
        });
      });
  });

  return entities.map(e => ({
    ...e,
    nickname: resolveOwnerNicknameFromData(e.owner_type, e.owner_id, users, businesses),
  }));
}

function getActiveFoldersFromData(accountOwnerId: number, foldersData: FolderRow[], clientId?: number | string | null): FolderRow[] {
  return foldersData.filter(f =>
    // eslint-disable-next-line eqeqeq
    f.account_owner_id == accountOwnerId && f.is_deleted !== 1 &&
    // Client's Accounts: only this client's folders (dms_client_folders.client_id)
    // eslint-disable-next-line eqeqeq
    (clientId == null || (f as any).client_id == clientId)
  );
}

function getFolderOwnersFromData(folderId: number, folderOwnersData: FolderOwnerRow[]): FolderOwnerRow[] {
  // eslint-disable-next-line eqeqeq
  return folderOwnersData.filter(o => o.folder_id == folderId && o.is_deleted !== 1);
}

function buildBreadcrumb(folder: FolderRow, allFolders: FolderRow[]): string {
  const parts: string[] = [folder.folder_name];
  let current = folder;
  let depth = 0;
  while (current.parent_folder_id != null && depth < 20) {
    // eslint-disable-next-line eqeqeq
    const parent = allFolders.find(f => f.folder_id == current.parent_folder_id);
    if (!parent) break;
    parts.unshift(parent.folder_name);
    current = parent;
    depth++;
  }
  return parts.join(' > ');
}

function computeFolderPath(folderId: number, allFolders: FolderRow[]): string {
  const parts: number[] = [folderId];
  // eslint-disable-next-line eqeqeq
  let current = allFolders.find(f => f.folder_id == folderId);
  let depth = 0;
  while (current && current.parent_folder_id != null && depth < 20) {
    parts.unshift(Number(current.parent_folder_id));
    // eslint-disable-next-line eqeqeq
    current = allFolders.find(f => f.folder_id == current!.parent_folder_id);
    depth++;
  }
  return '/' + parts.join('/') + '/';
}

function computeFolderDepth(parentFolderId: number | null, allFolders: FolderRow[]): number {
  if (parentFolderId == null) return 0;
  // eslint-disable-next-line eqeqeq
  const parent = allFolders.find(f => f.folder_id == parentFolderId);
  return parent ? (Number(parent.folder_depth) || 0) + 1 : 0;
}

function getDescendantIds(folderId: number, allFolders: FolderRow[]): number[] {
  const ids: number[] = [];
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    // eslint-disable-next-line eqeqeq
    const children = allFolders.filter(f => f.parent_folder_id == current && f.is_deleted !== 1);
    for (const c of children) {
      ids.push(Number(c.folder_id));
      queue.push(Number(c.folder_id));
    }
  }
  return ids;
}

function resolveOwnerNameFromData(ownerType: string, ownerId: number, usersData: Record<string, any>[], businessesData: Record<string, any>[]): string {
  if (ownerType === 'account_owner' || ownerType === 'family_member' || ownerType === 'client_personal') {
    // eslint-disable-next-line eqeqeq
    const u = usersData.find((u: any) => u.user_id == ownerId);
    return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email : `User #${ownerId}`;
  }
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    const b = businessesData.find((b: any) => b.business_id == ownerId);
    return b ? b.busi_legal_name || b.busi_operating_name : `Business #${ownerId}`;
  }
  return `${ownerType}:${ownerId}`;
}

function sortFoldersTree(folders: FolderRow[]): FolderRow[] {
  const roots = folders.filter(f => f.parent_folder_id == null);
  const result: FolderRow[] = [];
  const addChildren = (parentId: number | null, depth: number) => {
    const children = folders
      // eslint-disable-next-line eqeqeq
      .filter(f => f.parent_folder_id == parentId)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.folder_name.localeCompare(b.folder_name));
    for (const c of children) {
      result.push(c);
      addChildren(Number(c.folder_id), depth + 1);
    }
  };
  roots.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.folder_name.localeCompare(b.folder_name));
  for (const r of roots) {
    result.push(r);
    addChildren(Number(r.folder_id), 1);
  }
  return result;
}

function getAncestorChain(folder: FolderRow, allFolders: FolderRow[]): FolderRow[] {
  const chain: FolderRow[] = [];
  let current: FolderRow | undefined = folder;
  let depth = 0;
  while (current && depth < 20) {
    chain.unshift(current);
    if (current.parent_folder_id == null) break;
    // eslint-disable-next-line eqeqeq
    current = allFolders.find(f => f.folder_id == current!.parent_folder_id);
    depth++;
  }
  return chain;
}

// =============================================
// Component
// =============================================
interface ManageFoldersTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}

const ManageFoldersTab: React.FC<ManageFoldersTabProps> = ({ viewMode = 'owner', selectedClientId = null }) => {
  // Client's Accounts uses isolated per-client folder tables; Your Accounts/DMS
  // use the owner's folder tables. All folder CRUD below goes through these names.
  const FOLDERS_TABLE = viewMode === 'clients' ? 'dms_client_folders' : 'dms_folders';
  const FOLDER_OWNERS_TABLE = viewMode === 'clients' ? 'dms_client_folder_owners' : 'dms_folder_owners';
  // dms_client_folders requires client_id; dms_folders has no such column. Spread
  // CLIENT_FOLDER_FIELDS into folder inserts so it's only set in clients mode.
  const CLIENT_FOLDER_FIELDS: Record<string, any> = viewMode === 'clients' && selectedClientId != null
    ? { client_id: Number(selectedClientId) }
    : {};
  // ---- Loaded table data ----
  const [usersData, setUsersData] = useState<Record<string, any>[]>(() => getTableData('users'));
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>(() => getTableData('user_businesses'));
  const [foldersRaw, setFoldersRaw] = useState<FolderRow[]>(() => getTableData(FOLDERS_TABLE) as FolderRow[]);
  const [folderOwnersRaw, setFolderOwnersRaw] = useState<FolderOwnerRow[]>(() => getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[]);
  const [documentsData, setDocumentsData] = useState<Record<string, any>[]>(() => getTableData('dms_documents'));
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await checkApi();
      const [users, businesses, folders, folderOwners, docs] = await Promise.all([
        loadTable('users'),
        loadTable('user_businesses'),
        loadTable(FOLDERS_TABLE),
        loadTable(FOLDER_OWNERS_TABLE),
        loadTable('dms_documents'),
      ]);
      if (cancelled) return;
      setUsersData(users);
      setBusinessesData(businesses);
      setFoldersRaw(folders as FolderRow[]);
      setFolderOwnersRaw(folderOwners as FolderOwnerRow[]);
      setDocumentsData(docs);
      setDataLoaded(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // ---- Auth ----
  const currentUser = useMemo(() => getCurrentUserFromData(usersData), [usersData]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    return getAccountOwnerForUserFromData(currentUser.user_id, usersData); // logged-in user's own account (multi-tenant safe)
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;
  const isAccountOwner = currentUser?.owner_id == null;

  // ---- Owner entities ----
  const ownerEntities = useMemo(() => {
    if (!accountOwnerId) return [];
    const all = getOwnerEntitiesFromData(accountOwnerId, usersData, businessesData);
    if (viewMode === 'clients') {
      if (!selectedClientId) return [];
      // Filter to only the selected client + their family + their businesses
      const clientEntities = all.filter(e => {
        if (e.owner_type === 'client_personal' && e.owner_id == selectedClientId) return true;
        if (e.owner_type === 'client_personal' && e.badge.includes("'s Family")) {
          // Check if this family member's owner_id matches the selected client
          const familyUser = usersData.find((u: any) => u.user_id == e.owner_id);
          return familyUser && familyUser.owner_id == selectedClientId;
        }
        if (e.owner_type === 'client_business') {
          const biz = businessesData.find((b: any) => b.business_id == e.owner_id);
          return biz && biz.owner_id == selectedClientId;
        }
        return false;
      });
      return clientEntities;
    }
    return all.filter(e => e.owner_type !== 'client_personal' && e.owner_type !== 'client_business');
  }, [accountOwnerId, usersData, businessesData, viewMode, selectedClientId]);

  // ---- State ----
  const [folders, setFolders] = useState<FolderRow[]>(() => []);
  const [folderLevels, setFolderLevels] = useState<FolderLevel[]>([
    { inputText: '', selectedFolder: null, showAutocomplete: false }
  ]);
  const [mode, setMode] = useState<'idle' | 'delete'>('idle');
  const [editingFolder, setEditingFolder] = useState<FolderRow | null>(null);
  const [form, setForm] = useState<FolderFormFields>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formOwners, setFormOwners] = useState<OwnerEntity[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [showAddOwnerPicker, setShowAddOwnerPicker] = useState(false);
  const [deleteAction, setDeleteAction] = useState<'all' | 'self'>('all');
  const [showMoveTree, setShowMoveTree] = useState(false);
  const [moveTarget, setMoveTarget] = useState<{ id: number | null; name: string } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<Record<number, number>>({});
  const [renamePrompt, setRenamePrompt] = useState<{ levelIndex: number; oldName: string; newName: string } | null>(null);
  const levelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const formCardRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Derive active folders from raw data (scoped to the client in clients mode)
  useEffect(() => {
    if (accountOwnerId) {
      const clientScope = viewMode === 'clients' ? selectedClientId : null;
      setFolders(getActiveFoldersFromData(accountOwnerId, foldersRaw, clientScope));
    }
  }, [accountOwnerId, foldersRaw, viewMode, selectedClientId]);

  const refreshFolders = useCallback(async () => {
    if (!accountOwnerId) return;
    const [newFolders, newFolderOwners] = await Promise.all([
      loadTable(FOLDERS_TABLE),
      loadTable(FOLDER_OWNERS_TABLE),
    ]);
    setFoldersRaw(newFolders as FolderRow[]);
    setFolderOwnersRaw(newFolderOwners as FolderOwnerRow[]);
  }, [accountOwnerId]);

  // Close autocomplete on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (formCardRef.current && !formCardRef.current.contains(e.target as Node)) {
        setFolderLevels(prev => prev.map(l => ({ ...l, showAutocomplete: false })));
        setShowAddOwnerPicker(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Auto-focus the deepest empty sub-level input after folder selection
  useEffect(() => {
    const lastIdx = folderLevels.length - 1;
    if (lastIdx > 0 && !folderLevels[lastIdx].selectedFolder && !folderLevels[lastIdx].inputText) {
      requestAnimationFrame(() => {
        const wrapper = levelRefs.current[lastIdx];
        const input = wrapper?.querySelector('input') as HTMLInputElement | null;
        input?.focus();
      });
    }
  }, [folderLevels.length]);

  // ---- Derived state ----
  const activeLevel = folderLevels.length - 1;

  const isNewAtAnyLevel = useMemo(() => {
    return folderLevels.some(l => l.inputText.trim() && !l.selectedFolder);
  }, [folderLevels]);

  const deepestSelected = useMemo(() => {
    for (let i = folderLevels.length - 1; i >= 0; i--) {
      if (folderLevels[i].selectedFolder) return folderLevels[i].selectedFolder;
    }
    return null;
  }, [folderLevels]);

  const newLevelIndex = useMemo(() => {
    for (let i = 0; i < folderLevels.length; i++) {
      if (folderLevels[i].inputText.trim() && !folderLevels[i].selectedFolder) return i;
    }
    return -1;
  }, [folderLevels]);

  const isEditing = editingFolder !== null;

  // ---- Owner inheritance rule ----
  // A sub-folder under a NON-umbrella (user-owned) parent must be owned by exactly
  // that parent's owner(s); only an umbrella parent lets a child pick a fresh owner.
  // (Fixes: a Hazel-owned folder could get a Neelima-owned sub-folder.)
  const inheritedParentOwners = useMemo<OwnerEntity[] | null>(() => {
    if (newLevelIndex <= 0) return null;                       // root level → free / umbrella
    const parent = folderLevels[newLevelIndex - 1].selectedFolder;
    if (!parent || Number(parent.umbrella_folder) === 1) return null;  // umbrella parent → free pick
    return getFolderOwnersFromData(Number(parent.folder_id), folderOwnersRaw).map(o => ({
      owner_type: o.owner_type as OwnerEntity['owner_type'],
      owner_id: Number(o.owner_id),
      display_name: resolveOwnerNameFromData(o.owner_type, Number(o.owner_id), usersData, businessesData),
      nickname: resolveOwnerNicknameFromData(o.owner_type, Number(o.owner_id), usersData, businessesData),
      badge: o.owner_type === 'account_owner' ? 'Owner'
        : o.owner_type === 'family_member' ? 'Family'
        : o.owner_type === 'business' ? 'Business'
        : o.owner_type === 'client_personal' ? 'Client' : 'Client Biz',
    }));
  }, [newLevelIndex, folderLevels, folderOwnersRaw, usersData, businessesData]);
  const ownersLocked = inheritedParentOwners !== null;

  // Keep a locked sub-folder's owners pinned to the inherited parent owners.
  useEffect(() => {
    if (!ownersLocked || !inheritedParentOwners) return;
    setFormOwners(prev => {
      const same = prev.length === inheritedParentOwners.length &&
        inheritedParentOwners.every(p => prev.some(o => o.owner_type === p.owner_type && o.owner_id === p.owner_id));
      return same ? prev : inheritedParentOwners;
    });
  }, [ownersLocked, inheritedParentOwners]);

  // Detect if the editing folder's name has been changed
  const nameChanged = useMemo(() => {
    if (!editingFolder) return false;
    return form.folder_name.trim() !== editingFolder.folder_name.trim();
  }, [editingFolder, form.folder_name]);

  // Review button enabled when there's a meaningful action to take
  const canReview = useMemo(() => {
    // Creating a new folder at any level
    if (isNewAtAnyLevel) {
      const createIdx = folderLevels.findIndex(l => l.inputText.trim() && !l.selectedFolder);
      // Root-level folders can be umbrella (no owner needed)
      if (createIdx === 0) return true;
      // Sub-level folders require at least one owner
      return formOwners.length > 0;
    }
    // Editing an existing folder — only if something actually changed
    if (isEditing && editingFolder) {
      const hasNameChange = form.folder_name.trim() !== editingFolder.folder_name.trim();
      const hasDescChange = form.folder_description.trim() !== (editingFolder.folder_description || '').trim();
      const hasIconChange = form.folder_icon !== (editingFolder.folder_icon || '');
      const hasColorChange = form.folder_color !== (editingFolder.folder_color || '');
      const anyFieldChange = hasNameChange || hasDescChange || hasIconChange || hasColorChange;
      if (!anyFieldChange) return false;
      // Sub-folders require owner
      if (editingFolder.parent_folder_id != null && formOwners.length === 0) return false;
      return true;
    }
    return false;
  }, [isNewAtAnyLevel, isEditing, editingFolder, folderLevels, formOwners, form]);

  // Compute folders available at each level
  const foldersForLevel = useMemo(() => {
    return folderLevels.map((level, idx) => {
      const parentId = idx === 0 ? null : folderLevels[idx - 1].selectedFolder?.folder_id ?? null;
      if (idx > 0 && parentId === null) return [];

      let candidates = folders.filter(f => {
        if (idx === 0) return f.parent_folder_id == null;
        // eslint-disable-next-line eqeqeq
        return f.parent_folder_id == parentId;
      });

      // No umbrella folders in sub-levels
      if (idx > 0) {
        candidates = candidates.filter(f => Number(f.umbrella_folder) !== 1);
      }

      if (!level.inputText.trim()) return candidates;
      const lower = level.inputText.toLowerCase();
      return candidates.filter(f => f.folder_name.toLowerCase().includes(lower));
    });
  }, [folderLevels, folders]);

  // Breadcrumb for the current path
  const breadcrumbPath = useMemo(() => {
    return folderLevels
      .filter(l => l.selectedFolder || l.inputText.trim())
      .map(l => l.selectedFolder?.folder_name || l.inputText.trim())
      .join(' > ');
  }, [folderLevels]);

  // ---- Level handlers ----
  const handleLevelInput = useCallback((levelIndex: number, text: string) => {
    setFormDirty(true);
    setFolderLevels(prev => {
      const next = prev.slice(0, levelIndex + 1);
      next[levelIndex] = {
        inputText: text,
        selectedFolder: null,
        showAutocomplete: true,
      };
      return next;
    });
    setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
    setFormErrors({});
    setShowSummary(false);
    if (levelIndex === 0 && !text.trim()) {
      setForm(EMPTY_FORM);
      setFormOwners([]);
      setEditingFolder(null);
    }
  }, []);

  const handleSelectFolder = useCallback((levelIndex: number, folder: FolderRow) => {
    setFolderLevels(prev => {
      const next = prev.slice(0, levelIndex + 1);
      next[levelIndex] = {
        inputText: folder.folder_name,
        selectedFolder: folder,
        showAutocomplete: false,
      };
      // Add empty sub-level if folder has children or can have children
      const hasChildren = folders.some(f =>
        // eslint-disable-next-line eqeqeq
        f.parent_folder_id == folder.folder_id && f.is_deleted !== 1
      );
      if (next.length <= MAX_SUBFOLDER_LEVELS) {
        next.push({ inputText: '', selectedFolder: null, showAutocomplete: false });
      }
      return next;
    });

    // Populate form for editing the selected folder
    setForm({
      folder_name: folder.folder_name || '',
      folder_description: folder.folder_description || '',
      folder_icon: folder.folder_icon || '',
      folder_color: folder.folder_color || '',
    });

    const owners = getFolderOwnersFromData(Number(folder.folder_id), folderOwnersRaw);
    const resolved: OwnerEntity[] = owners.map(o => ({
      owner_type: o.owner_type as OwnerEntity['owner_type'],
      owner_id: Number(o.owner_id),
      display_name: resolveOwnerNameFromData(o.owner_type, Number(o.owner_id), usersData, businessesData),
      badge: o.owner_type === 'account_owner' ? 'Owner'
        : o.owner_type === 'family_member' ? 'Family'
        : o.owner_type === 'business' ? 'Business'
        : o.owner_type === 'client_personal' ? 'Client'
        : 'Client Biz',
    }));
    setFormOwners(resolved);
    setEditingFolder(folder);
    setFormErrors({});
    setShowSummary(false);
  }, [folders, folderOwnersRaw, usersData, businessesData]);

  const handleClearLevel = useCallback((levelIndex: number) => {
    setFolderLevels(prev => {
      if (levelIndex === 0) {
        return [{ inputText: '', selectedFolder: null, showAutocomplete: false }];
      }
      return prev.slice(0, levelIndex).concat([{ inputText: '', selectedFolder: null, showAutocomplete: false }]);
    });
    if (levelIndex === 0) {
      setForm(EMPTY_FORM);
      setFormOwners([]);
      setEditingFolder(null);
    } else {
      // Load the parent folder's data for editing
      const parentLevel = folderLevels[levelIndex - 1];
      if (parentLevel?.selectedFolder) {
        const folder = parentLevel.selectedFolder;
        setForm({
          folder_name: folder.folder_name || '',
          folder_description: folder.folder_description || '',
          folder_icon: folder.folder_icon || '',
          folder_color: folder.folder_color || '',
        });
        const owners = getFolderOwnersFromData(Number(folder.folder_id), folderOwnersRaw);
        const resolved: OwnerEntity[] = owners.map(o => ({
          owner_type: o.owner_type as OwnerEntity['owner_type'],
          owner_id: Number(o.owner_id),
          display_name: resolveOwnerNameFromData(o.owner_type, Number(o.owner_id), usersData, businessesData),
          badge: o.owner_type === 'account_owner' ? 'Owner'
            : o.owner_type === 'family_member' ? 'Family'
            : o.owner_type === 'business' ? 'Business'
            : o.owner_type === 'client_personal' ? 'Client'
            : 'Client Biz',
        }));
        setFormOwners(resolved);
        setEditingFolder(folder);
      }
    }
    setFormErrors({});
    setShowSummary(false);
  }, [folderLevels, folderOwnersRaw, usersData, businessesData]);

  // ---- Keyboard navigation for dropdowns ----
  const handleLevelKeyDown = useCallback((levelIndex: number, e: React.KeyboardEvent, levelFolders: FolderRow[]) => {
    const currentIdx = highlightedIndex[levelIndex] ?? -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(currentIdx + 1, levelFolders.length - 1);
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: next }));
      const item = dropdownRefs.current[levelIndex]?.children[next] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.max(currentIdx - 1, 0);
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: next }));
      const item = dropdownRefs.current[levelIndex]?.children[next] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (currentIdx >= 0 && currentIdx < levelFolders.length) {
        handleSelectFolder(levelIndex, levelFolders[currentIdx]);
        setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
      }
    } else if (e.key === 'Escape') {
      setFolderLevels(prev => {
        const next = [...prev];
        next[levelIndex] = { ...next[levelIndex], showAutocomplete: false };
        return next;
      });
      setHighlightedIndex(prev => ({ ...prev, [levelIndex]: -1 }));
    }
  }, [highlightedIndex, handleSelectFolder]);

  const closeForm = useCallback(() => {
    setFormDirty(false);
    setFolderLevels([{ inputText: '', selectedFolder: null, showAutocomplete: false }]);
    setMode('idle');
    setEditingFolder(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setFormOwners([]);
    setShowSummary(false);
    setShowAddOwnerPicker(false);
    setShowMoveTree(false);
    setMoveTarget(null);
    setRenamePrompt(null);
  }, []);

  // ---- Move folder logic ----
  const moveTargetTree = useMemo(() => {
    if (!editingFolder || !showMoveTree) return [];
    const excludeIds = new Set([
      Number(editingFolder.folder_id),
      ...getDescendantIds(Number(editingFolder.folder_id), folders),
    ]);
    return sortFoldersTree(folders.filter(f => !excludeIds.has(Number(f.folder_id))));
  }, [editingFolder, showMoveTree, folders]);

  const moveMaxDepthOk = useCallback((targetParentId: number | null): boolean => {
    if (!editingFolder) return false;
    const subtreeDepth = getDescendantIds(Number(editingFolder.folder_id), folders)
      .reduce((max, id) => {
        // eslint-disable-next-line eqeqeq
        const f = folders.find(f => f.folder_id == id);
        return f ? Math.max(max, Number(f.folder_depth)) : max;
      }, Number(editingFolder.folder_depth));
    const relativeDepth = subtreeDepth - Number(editingFolder.folder_depth);

    let targetDepth = 0;
    if (targetParentId !== null) {
      // eslint-disable-next-line eqeqeq
      const parent = folders.find(f => f.folder_id == targetParentId);
      targetDepth = parent ? Number(parent.folder_depth) + 1 : 0;
    }
    return (targetDepth + relativeDepth) <= MAX_SUBFOLDER_LEVELS;
  }, [editingFolder, folders]);

  const handleMoveFolder = useCallback(async () => {
    if (!editingFolder || !moveTarget || !currentUser || !accountOwnerId) return;

    const newParentId = moveTarget.id;
    const folderId = Number(editingFolder.folder_id);

    if (Number(editingFolder.parent_folder_id) === newParentId ||
      (editingFolder.parent_folder_id == null && newParentId === null)) {
      setSavedMsg('Folder is already in that location.');
      setTimeout(() => setSavedMsg(''), 3000);
      setShowMoveTree(false);
      setMoveTarget(null);
      return;
    }

    if (!moveMaxDepthOk(newParentId)) {
      setFormErrors({ move: `Cannot move here — would exceed the maximum ${MAX_SUBFOLDER_LEVELS + 1} folder levels.` });
      return;
    }

    const now = NOW_ISO();
    const allFoldersLocal = [...foldersRaw] as FolderRow[];

    // Collect all folders to update (moved folder + its descendants)
    const descendantIds = getDescendantIds(folderId, allFoldersLocal);
    const idsToUpdate = [folderId, ...descendantIds];

    if (isApiReady()) {
      // Update the moved folder's parent
      await saveRow(FOLDERS_TABLE, editingFolder.folder_id, {
        parent_folder_id: newParentId,
        updated_at: now,
      }, 'folder_id');

      // Update the local copy for path/depth recalculation
      // eslint-disable-next-line eqeqeq
      const movedIdx = allFoldersLocal.findIndex(f => f.folder_id == folderId);
      if (movedIdx >= 0) {
        allFoldersLocal[movedIdx] = { ...allFoldersLocal[movedIdx], parent_folder_id: newParentId };
      }

      // Recalculate depth and path for all affected folders
      for (const id of idsToUpdate) {
        const depth = computeFolderDepth(
          // eslint-disable-next-line eqeqeq
          id === folderId ? newParentId : allFoldersLocal.find(f => f.folder_id == id)?.parent_folder_id ?? null,
          allFoldersLocal
        );
        const path = computeFolderPath(id, allFoldersLocal);
        await saveRow(FOLDERS_TABLE, id, {
          folder_depth: depth,
          folder_path: path,
          updated_at: now,
        }, 'folder_id');
      }

      // Update umbrella status: if moved to root with no owners, mark as umbrella; if moved under a parent, clear umbrella
      if (newParentId === null) {
        const owners = folderOwnersRaw.filter(
          // eslint-disable-next-line eqeqeq
          o => o.folder_id == folderId && o.is_deleted !== 1
        );
        if (owners.length === 0) {
          await saveRow(FOLDERS_TABLE, editingFolder.folder_id, { umbrella_folder: 1, updated_at: now }, 'folder_id');
        }
      } else {
        if (Number(editingFolder.umbrella_folder) === 1) {
          await saveRow(FOLDERS_TABLE, editingFolder.folder_id, { umbrella_folder: 0, updated_at: now }, 'folder_id');
        }
      }
    } else {
      // localStorage fallback
      const allFolders = getTableData(FOLDERS_TABLE) as FolderRow[];
      // eslint-disable-next-line eqeqeq
      const movedIdx = allFolders.findIndex(f => f.folder_id == folderId);
      if (movedIdx >= 0) {
        allFolders[movedIdx].parent_folder_id = newParentId;
        allFolders[movedIdx].updated_at = now;
      }
      for (const id of idsToUpdate) {
        // eslint-disable-next-line eqeqeq
        const fi = allFolders.findIndex(f => f.folder_id == id);
        if (fi >= 0) {
          allFolders[fi].folder_depth = computeFolderDepth(
            id === folderId ? newParentId : allFolders[fi].parent_folder_id,
            allFolders
          );
          allFolders[fi].folder_path = computeFolderPath(id, allFolders);
          allFolders[fi].updated_at = now;
        }
      }
      if (movedIdx >= 0) {
        if (newParentId === null) {
          const owners = (getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[]).filter(
            // eslint-disable-next-line eqeqeq
            o => o.folder_id == folderId && o.is_deleted !== 1
          );
          allFolders[movedIdx].umbrella_folder = owners.length === 0 ? 1 : 0;
        } else {
          allFolders[movedIdx].umbrella_folder = 0;
        }
      }
      saveTableData(FOLDERS_TABLE, allFolders);
    }

    await refreshFolders();

    const oldLocation = editingFolder.parent_folder_id == null
      ? 'Root'
      // eslint-disable-next-line eqeqeq
      : folders.find(f => f.folder_id == editingFolder.parent_folder_id)?.folder_name || 'unknown';
    const newLocation = newParentId === null
      ? 'Root'
      // eslint-disable-next-line eqeqeq
      : folders.find(f => f.folder_id == newParentId)?.folder_name || moveTarget.name;

    const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
    addNotification({
      account_owner_id: accountOwnerId,
      type: 'folder_moved',
      title: 'Folder moved',
      message: `${userName} moved folder "${editingFolder.folder_name}" from "${oldLocation}" to "${newLocation}".`,
      actor_user_id: currentUser.user_id,
      actor_name: userName,
    });

    setSavedMsg(`Folder moved to ${newLocation}!`);
    setTimeout(() => setSavedMsg(''), 3000);
    setShowMoveTree(false);
    setMoveTarget(null);
    closeForm();
  }, [editingFolder, moveTarget, currentUser, accountOwnerId, folders, foldersRaw, folderOwnersRaw, refreshFolders, closeForm, moveMaxDepthOk]);

  // ---- Form handlers ----
  const handleFormChange = useCallback((field: keyof FolderFormFields, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: '' }));
    setShowSummary(false);
  }, []);

  const openDeleteConfirm = useCallback((folder: FolderRow) => {
    setEditingFolder(folder);
    setMode('delete');
    setDeleteAction(isAccountOwner ? 'all' : 'self');
  }, [isAccountOwner]);

  // ---- Validate ----
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // Find the level being created
    const createIdx = folderLevels.findIndex(l => l.inputText.trim() && !l.selectedFolder);
    if (createIdx >= 0) {
      const name = folderLevels[createIdx].inputText.trim();
      if (!name) {
        errors.folder_name = 'Folder name is required';
      }

      // Duplicate check at this level
      const parentId = createIdx === 0 ? null : folderLevels[createIdx - 1].selectedFolder?.folder_id ?? null;
      if (createIdx > 0 && parentId === null) {
        errors.folder_name = 'Please select a parent folder first';
      } else {
        const dup = folders.find(f =>
          f.folder_name.toLowerCase() === name.toLowerCase() &&
          // eslint-disable-next-line eqeqeq
          f.parent_folder_id == parentId &&
          f.is_deleted !== 1
        );
        if (dup) {
          errors.folder_name = 'A folder with this name already exists at this level';
        }
      }

      // Owner rules
      if (createIdx > 0 && formOwners.length === 0) {
        errors.owners = 'Sub-folders require at least one owner';
      }
    } else if (isEditing) {
      if (!form.folder_name.trim()) {
        errors.folder_name = 'Folder name is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [folderLevels, folders, formOwners, form, isEditing]);

  // ---- Review ----
  const handleReview = useCallback(() => {
    if (validateForm()) {
      setShowSummary(true);
    }
  }, [validateForm]);

  // ---- Save (create or edit) ----
  const handleSave = useCallback(async () => {
    if (!validateForm() || !accountOwnerId || !currentUser) return;

    const now = NOW_ISO();
    const createIdx = folderLevels.findIndex(l => l.inputText.trim() && !l.selectedFolder);

    if (createIdx >= 0) {
      // CREATE mode
      const folderName = folderLevels[createIdx].inputText.trim();
      const parentId = createIdx === 0 ? null : Number(folderLevels[createIdx - 1].selectedFolder!.folder_id);
      const isUmbrella = formOwners.length === 0 && createIdx === 0 ? 1 : 0;
      // A sub-folder under a non-umbrella parent is forced to the parent's owner(s).
      const ownersToPersist = ownersLocked && inheritedParentOwners ? inheritedParentOwners : formOwners;

      if (isApiReady()) {
        const allFoldersLocal = [...foldersRaw] as FolderRow[];
        const depth = computeFolderDepth(parentId, allFoldersLocal);
        const createdFolder = await addRow(FOLDERS_TABLE, {
          account_owner_id: accountOwnerId,
          ...CLIENT_FOLDER_FIELDS,
          parent_folder_id: parentId,
          folder_name: folderName,
          folder_path: '',
          folder_depth: depth,
          folder_description: form.folder_description.trim(),
          folder_icon: form.folder_icon,
          folder_color: form.folder_color,
          umbrella_folder: isUmbrella,
          sort_order: 0,
          is_active: 1,
          is_deleted: 0,
          created_by: currentUser.user_id,
          created_at: now,
          updated_at: now,
        }, 'folder_id');

        const newId = createdFolder.folder_id;
        const tempFolders = [...allFoldersLocal, createdFolder as FolderRow];
        const folderPath = computeFolderPath(Number(newId), tempFolders as FolderRow[]);
        await saveRow(FOLDERS_TABLE, newId, { folder_path: folderPath }, 'folder_id');

        for (let idx = 0; idx < ownersToPersist.length; idx++) {
          const oe = ownersToPersist[idx];
          await addRow(FOLDER_OWNERS_TABLE, {
            folder_id: Number(newId),
            owner_type: oe.owner_type,
            owner_id: oe.owner_id,
            is_primary: idx === 0 ? 1 : 0,
            access_level: 'manage',
            is_active: 1,
            is_deleted: 0,
            added_by: currentUser.user_id,
            created_at: now,
            updated_at: now,
          }, 'folder_owner_id');
        }
      } else {
        const allFolders = getTableData(FOLDERS_TABLE) as FolderRow[];
        const allOwners = getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[];
        const newId = getNextId(FOLDERS_TABLE, allFolders);
        const depth = computeFolderDepth(parentId, allFolders);
        const newFolder: FolderRow = {
          ...CLIENT_FOLDER_FIELDS,
          folder_id: newId,
          account_owner_id: accountOwnerId,
          parent_folder_id: parentId,
          folder_name: folderName,
          folder_path: '',
          folder_depth: depth,
          folder_description: form.folder_description.trim(),
          folder_icon: form.folder_icon,
          folder_color: form.folder_color,
          umbrella_folder: isUmbrella,
          sort_order: 0,
          is_active: 1,
          is_deleted: 0,
          created_by: currentUser.user_id,
          created_at: now,
          updated_at: now,
        };
        allFolders.push(newFolder);
        newFolder.folder_path = computeFolderPath(newId, allFolders);
        saveTableData(FOLDERS_TABLE, allFolders);

        ownersToPersist.forEach((oe, idx) => {
          const ownerId = getNextId(FOLDER_OWNERS_TABLE, allOwners);
          allOwners.push({
            folder_owner_id: ownerId + idx,
            folder_id: newId,
            owner_type: oe.owner_type,
            owner_id: oe.owner_id,
            is_primary: idx === 0 ? 1 : 0,
            access_level: 'manage',
            is_active: 1,
            is_deleted: 0,
            added_by: currentUser.user_id,
            created_at: now,
            updated_at: now,
          });
        });
        saveTableData(FOLDER_OWNERS_TABLE, allOwners);
      }

      await refreshFolders();
      setSavedMsg('Folder created!');
      setTimeout(() => setSavedMsg(''), 3000);

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'folder_created',
        title: 'Folder created',
        message: `${userName} created folder "${folderName}".`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      closeForm();

    } else if (isEditing && editingFolder) {
      // EDIT mode
      const folderId = Number(editingFolder.folder_id);
      const parentId = editingFolder.parent_folder_id;

      if (isApiReady()) {
        const allFoldersLocal = [...foldersRaw] as FolderRow[];
        // eslint-disable-next-line eqeqeq
        const folderIdx = allFoldersLocal.findIndex(f => f.folder_id == editingFolder.folder_id);
        if (folderIdx < 0) return;

        allFoldersLocal[folderIdx] = {
          ...allFoldersLocal[folderIdx],
          folder_name: form.folder_name.trim(),
        };
        const folderPath = computeFolderPath(folderId, allFoldersLocal);

        await saveRow(FOLDERS_TABLE, editingFolder.folder_id, {
          folder_name: form.folder_name.trim(),
          folder_description: form.folder_description.trim(),
          folder_icon: form.folder_icon,
          folder_color: form.folder_color,
          updated_at: now,
        }, 'folder_id');

        // Sync owners
        const existingOwners = folderOwnersRaw.filter(
          // eslint-disable-next-line eqeqeq
          o => o.folder_id == editingFolder.folder_id && o.is_deleted !== 1
        );
        for (const eo of existingOwners) {
          const stillExists = formOwners.some(
            fo => fo.owner_type === eo.owner_type && fo.owner_id === Number(eo.owner_id)
          );
          if (!stillExists) {
            await saveRow(FOLDER_OWNERS_TABLE, eo.folder_owner_id, {
              is_deleted: 1,
              updated_at: now,
            }, 'folder_owner_id');
          }
        }
        for (const fo of formOwners) {
          const alreadyExists = existingOwners.some(
            eo => eo.owner_type === fo.owner_type && Number(eo.owner_id) === fo.owner_id
          );
          if (!alreadyExists) {
            await addRow(FOLDER_OWNERS_TABLE, {
              folder_id: folderId,
              owner_type: fo.owner_type,
              owner_id: fo.owner_id,
              is_primary: 0,
              access_level: 'manage',
              is_active: 1,
              is_deleted: 0,
              added_by: currentUser.user_id,
              created_at: now,
              updated_at: now,
            }, 'folder_owner_id');
          }
        }
      } else {
        const allFolders = getTableData(FOLDERS_TABLE) as FolderRow[];
        const allOwners = getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[];
        // eslint-disable-next-line eqeqeq
        const idx = allFolders.findIndex(f => f.folder_id == editingFolder.folder_id);
        if (idx < 0) return;

        allFolders[idx] = {
          ...allFolders[idx],
          folder_name: form.folder_name.trim(),
          folder_description: form.folder_description.trim(),
          folder_icon: form.folder_icon,
          folder_color: form.folder_color,
          updated_at: now,
        };
        saveTableData(FOLDERS_TABLE, allFolders);

        const existingOwners = allOwners.filter(
          // eslint-disable-next-line eqeqeq
          o => o.folder_id == editingFolder.folder_id && o.is_deleted !== 1
        );
        for (const eo of existingOwners) {
          const stillExists = formOwners.some(
            fo => fo.owner_type === eo.owner_type && fo.owner_id === Number(eo.owner_id)
          );
          if (!stillExists) {
            // eslint-disable-next-line eqeqeq
            const oi = allOwners.findIndex(o => o.folder_owner_id == eo.folder_owner_id);
            if (oi >= 0) {
              allOwners[oi].is_deleted = 1;
              allOwners[oi].updated_at = now;
            }
          }
        }
        for (const fo of formOwners) {
          const alreadyExists = existingOwners.some(
            eo => eo.owner_type === fo.owner_type && Number(eo.owner_id) === fo.owner_id
          );
          if (!alreadyExists) {
            const newOwnerId = getNextId(FOLDER_OWNERS_TABLE, allOwners);
            allOwners.push({
              folder_owner_id: newOwnerId,
              folder_id: folderId,
              owner_type: fo.owner_type,
              owner_id: fo.owner_id,
              is_primary: 0,
              access_level: 'manage',
              is_active: 1,
              is_deleted: 0,
              added_by: currentUser.user_id,
              created_at: now,
              updated_at: now,
            });
          }
        }
        saveTableData(FOLDER_OWNERS_TABLE, allOwners);
      }

      await refreshFolders();
      setSavedMsg('Folder updated!');
      setTimeout(() => setSavedMsg(''), 3000);

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'folder_edited',
        title: 'Folder edited',
        message: `${userName} edited folder "${form.folder_name.trim()}".`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      closeForm();
    }
  }, [folderLevels, form, formOwners, ownersLocked, inheritedParentOwners, editingFolder, isEditing, accountOwnerId, currentUser, folders, foldersRaw, folderOwnersRaw, refreshFolders, closeForm, validateForm]);

  // Expose this form's save to the unsaved-changes modal ("Save & continue").
  // Returns false if the form is invalid so the modal aborts the navigation.
  const saveFromModal = useCallback(async (): Promise<boolean> => {
    if (!validateForm()) return false;
    try { await handleSave(); return true; } catch { return false; }
  }, [validateForm, handleSave]);
  useEffect(() => {
    registerSaveHandler(saveFromModal);
    return () => registerSaveHandler(null);
  }, [saveFromModal]);

  // ---- Delete folder ----
  const handleDelete = useCallback(async () => {
    if (!editingFolder || !currentUser || !accountOwnerId) return;

    const now = NOW_ISO();
    const folderId = Number(editingFolder.folder_id);
    const allFoldersLocal = [...foldersRaw] as FolderRow[];
    const idsToDelete = [folderId, ...getDescendantIds(folderId, allFoldersLocal)];

    // eslint-disable-next-line eqeqeq
    const activeOwners = folderOwnersRaw.filter(o => o.folder_id == folderId && o.is_deleted !== 1);
    const isShared = activeOwners.length > 1;

    // Guard: a folder (or any sub-folder) that still holds documents can't be
    // deleted — it must be emptied first. Removing only one's own access to a
    // shared folder doesn't destroy anything, so that path stays allowed.
    const removingOwnAccessOnly = deleteAction === 'self' && isShared;
    if (!removingOwnAccessOnly) {
      // eslint-disable-next-line eqeqeq
      const docsInTree = documentsData.filter((d: any) => idsToDelete.some(id => d.dms_folders_id == id) && d.is_deleted !== 1).length;
      if (docsInTree > 0) {
        setSavedMsg(`Can't delete "${editingFolder.folder_name}" — empty its ${docsInTree} document${docsInTree !== 1 ? 's' : ''} first (move or delete them in Process Docs / Receipts).`);
        setTimeout(() => setSavedMsg(''), 5000);
        return;
      }
    }

    if (deleteAction === 'self' && isShared) {
      if (isApiReady()) {
        for (const id of idsToDelete) {
          const myOwnership = folderOwnersRaw.find(o =>
            // eslint-disable-next-line eqeqeq
            o.folder_id == id && o.is_deleted !== 1 &&
            // eslint-disable-next-line eqeqeq
            o.owner_id == currentUser.user_id
          );
          if (myOwnership) {
            await saveRow(FOLDER_OWNERS_TABLE, myOwnership.folder_owner_id, {
              is_deleted: 1,
              updated_at: now,
            }, 'folder_owner_id');
          }
        }
      } else {
        const allOwners = getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[];
        for (const id of idsToDelete) {
          const myOwnership = allOwners.find(o =>
            // eslint-disable-next-line eqeqeq
            o.folder_id == id && o.is_deleted !== 1 &&
            // eslint-disable-next-line eqeqeq
            o.owner_id == currentUser.user_id
          );
          if (myOwnership) {
            // eslint-disable-next-line eqeqeq
            const oi = allOwners.findIndex(ao => ao.folder_owner_id == myOwnership.folder_owner_id);
            if (oi >= 0) {
              allOwners[oi].is_deleted = 1;
              allOwners[oi].updated_at = now;
            }
          }
        }
        saveTableData(FOLDER_OWNERS_TABLE, allOwners);
      }

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'folder_access_removed',
        title: 'Folder access removed',
        message: `${userName} removed themselves from shared folder "${editingFolder.folder_name}".`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      await refreshFolders();
      setSavedMsg('Your access to the folder has been removed.');
      setTimeout(() => setSavedMsg(''), 3000);
    } else {
      if (isApiReady()) {
        for (const id of idsToDelete) {
          await saveRow(FOLDERS_TABLE, id, { is_deleted: 1, updated_at: now }, 'folder_id');
          const ownerRows = folderOwnersRaw.filter(o =>
            // eslint-disable-next-line eqeqeq
            o.folder_id == id && o.is_deleted !== 1
          );
          for (const o of ownerRows) {
            await saveRow(FOLDER_OWNERS_TABLE, o.folder_owner_id, {
              is_deleted: 1,
              updated_at: now,
            }, 'folder_owner_id');
          }
        }
      } else {
        const allFolders = getTableData(FOLDERS_TABLE) as FolderRow[];
        const allOwners = getTableData(FOLDER_OWNERS_TABLE) as FolderOwnerRow[];
        for (const id of idsToDelete) {
          // eslint-disable-next-line eqeqeq
          const fi = allFolders.findIndex(f => f.folder_id == id);
          if (fi >= 0) {
            allFolders[fi].is_deleted = 1;
            allFolders[fi].updated_at = now;
          }
          allOwners
            // eslint-disable-next-line eqeqeq
            .filter(o => o.folder_id == id && o.is_deleted !== 1)
            .forEach(o => {
              // eslint-disable-next-line eqeqeq
              const oi = allOwners.findIndex(ao => ao.folder_owner_id == o.folder_owner_id);
              if (oi >= 0) {
                allOwners[oi].is_deleted = 1;
                allOwners[oi].updated_at = now;
              }
            });
        }
        saveTableData(FOLDERS_TABLE, allFolders);
        saveTableData(FOLDER_OWNERS_TABLE, allOwners);
      }

      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'A user';
      addNotification({
        account_owner_id: accountOwnerId,
        type: 'folder_deleted',
        title: 'Folder deleted',
        message: `${userName} deleted folder "${editingFolder.folder_name}"${isShared ? ' (shared folder — removed for all owners)' : ''}.`,
        actor_user_id: currentUser.user_id,
        actor_name: userName,
      });

      await refreshFolders();
      setSavedMsg('Folder deleted.');
      setTimeout(() => setSavedMsg(''), 3000);
    }
    closeForm();
  }, [editingFolder, currentUser, accountOwnerId, deleteAction, foldersRaw, folderOwnersRaw, documentsData, refreshFolders, closeForm]);

  // ---- Owner management in form ----
  const removeFormOwner = useCallback((entity: OwnerEntity) => {
    setFormOwners(prev => prev.filter(o => !(o.owner_type === entity.owner_type && o.owner_id === entity.owner_id)));
  }, []);

  const addFormOwner = useCallback((entity: OwnerEntity) => {
    setFormOwners(prev => {
      if (prev.some(o => o.owner_type === entity.owner_type && o.owner_id === entity.owner_id)) return prev;
      return [...prev, entity];
    });
    setShowAddOwnerPicker(false);
  }, []);

  // ---- Delete info ----
  const deleteChildCount = useMemo(() => {
    if (!editingFolder) return 0;
    return getDescendantIds(Number(editingFolder.folder_id), folders).length;
  }, [editingFolder, folders]);

  const deleteDocCount = useMemo(() => {
    if (!editingFolder) return 0;
    const idsToCheck = [Number(editingFolder.folder_id), ...getDescendantIds(Number(editingFolder.folder_id), folders)];
    // eslint-disable-next-line eqeqeq
    return documentsData.filter((d: any) => idsToCheck.some(id => d.dms_folders_id == id) && d.is_deleted !== 1).length;
  }, [editingFolder, folders, documentsData]);

  const deleteFolderOwners = useMemo(() => {
    if (!editingFolder) return [];
    // eslint-disable-next-line eqeqeq
    return folderOwnersRaw.filter(o => o.folder_id == Number(editingFolder.folder_id) && o.is_deleted !== 1);
  }, [editingFolder, folderOwnersRaw]);

  const isSharedFolder = deleteFolderOwners.length > 1;

  const deleteFolderOwnerNames = useMemo(() => {
    return deleteFolderOwners.map(o => resolveOwnerNameFromData(o.owner_type, Number(o.owner_id), usersData, businessesData));
  }, [deleteFolderOwners, usersData, businessesData]);

  // Whether any action is possible (new name typed, or an existing folder selected at any level)
  const hasFormContent = isNewAtAnyLevel || isEditing || deepestSelected !== null;

  // ---- Loading / No user ----
  if (!currentUser || !accountOwner) {
    if (!dataLoaded) {
      return (
        <div className="mf-tab">
          <div className="mf-empty">
            <div className="mf-empty-icon"><FiFolder size={48} /></div>
            <p>Loading folders...</p>
          </div>
        </div>
      );
    }
    return (
      <div className="mf-tab">
        <div className="mf-empty">
          <div className="mf-empty-icon"><FiFolder size={48} /></div>
          <p>Please log in to manage folders.</p>
        </div>
      </div>
    );
  }

  // ---- Render ----
  return (
    <div className="mf-tab">
      <div className="mf-info-banner">
        Create and organize document folders. Type a folder name to search existing or create new.<br />
        Select an existing folder to edit or add sub-folders below it.
      </div>

      {savedMsg && (
        <div className="mf-action-bar">
          <span className="mf-saved-msg">{savedMsg}</span>
        </div>
      )}

      {/* ---- Always-visible Form ---- */}
      <div className="mf-form-card" ref={formCardRef}>

        {/* Cascading folder level inputs */}
        {folderLevels.map((level, idx) => {
          const levelFolders = foldersForLevel[idx] || [];
          const isRoot = idx === 0;
          const parentFolder = idx > 0 ? folderLevels[idx - 1].selectedFolder : null;

          // Only show this level if previous level has a selection (except root)
          if (idx > 0 && !folderLevels[idx - 1].selectedFolder) return null;

          return (
            <div
              key={idx}
              className="mf-field-group mf-level-wrapper"
              ref={el => { levelRefs.current[idx] = el; }}
              style={idx > 0 ? { marginLeft: idx * 16, borderLeft: '2px solid var(--gray-200)', paddingLeft: 12 } : undefined}
            >
              <label className="mf-field-label">
                {LEVEL_LABELS[idx] || `Level ${idx + 1}`}
                {isRoot && <span className="mf-required">*</span>}
                {idx > 0 && parentFolder && (
                  <span className="mf-folder-path-hint" style={{ marginLeft: 8, display: 'inline' }}>
                    in &ldquo;{parentFolder.folder_name}&rdquo;
                    {Number(parentFolder.umbrella_folder) === 1 && <span className="mf-umbrella-badge">&#9730;</span>}
                  </span>
                )}
              </label>

              <div className="mf-search-input-wrapper">
                <FiSearch className="mf-search-icon" size={16} />
                <input
                  className={`mf-search-input ${idx === newLevelIndex && formErrors.folder_name ? 'mf-input-error' : ''}`}
                  type="text"
                  placeholder={isRoot ? 'Type a folder name to search or create...' : 'Type a sub-folder name...'}
                  value={level.inputText}
                  readOnly={!!(level.selectedFolder && level.selectedFolder.folder_id !== editingFolder?.folder_id)}
                  style={level.selectedFolder && level.selectedFolder.folder_id !== editingFolder?.folder_id ? { background: '#f5f5f5', color: '#666' } : undefined}
                  onChange={e => {
                    if (level.selectedFolder && level.selectedFolder.folder_id === editingFolder?.folder_id) {
                      const val = e.target.value;
                      setFolderLevels(prev => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], inputText: val };
                        return next;
                      });
                      handleFormChange('folder_name', val);
                    } else if (!level.selectedFolder) {
                      handleLevelInput(idx, e.target.value);
                    }
                  }}
                  onKeyDown={e => {
                    if (level.showAutocomplete && !level.selectedFolder) {
                      handleLevelKeyDown(idx, e, levelFolders);
                    }
                  }}
                  onFocus={() => {
                    if (!level.selectedFolder) {
                      setFolderLevels(prev => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], showAutocomplete: true };
                        return next;
                      });
                    }
                  }}
                  onBlur={() => {
                    if (level.selectedFolder && level.selectedFolder.folder_id === editingFolder?.folder_id) {
                      const newName = level.inputText.trim();
                      const oldName = level.selectedFolder.folder_name.trim();
                      if (newName && newName !== oldName) {
                        setRenamePrompt({ levelIndex: idx, oldName, newName });
                      }
                    }
                  }}
                />
                {level.selectedFolder && (
                  <button
                    className="mf-search-move"
                    onClick={() => { setShowMoveTree(prev => !prev); setMoveTarget(null); setFormErrors({}); }}
                    title="Move folder to a different location"
                  >
                    <FiMove size={15} />
                  </button>
                )}
                {(level.inputText || level.selectedFolder) && (
                  <button className="mf-search-clear" onClick={() => handleClearLevel(idx)}>
                    <FiX size={16} />
                  </button>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {level.showAutocomplete && !level.selectedFolder && (
                <div className="mf-dropdown" ref={el => { dropdownRefs.current[idx] = el; }}>
                  {levelFolders.map((folder, fi) => (
                    <div
                      key={folder.folder_id}
                      className={`mf-dropdown-item ${(highlightedIndex[idx] ?? -1) === fi ? 'highlighted' : ''}`}
                      onClick={() => handleSelectFolder(idx, folder)}
                    >
                      <FiFolder className="mf-folder-icon-sm" size={14}
                        style={folder.folder_color ? { color: folder.folder_color } : undefined}
                      />
                      <span>{folder.folder_name}</span>
                      {Number(folder.umbrella_folder) === 1 && (
                        <span className="mf-umbrella-badge" title="Umbrella Folder">&#9730;</span>
                      )}
                      {isAccountOwner && (
                        <button
                          className="mf-btn-icon danger"
                          onClick={e => { e.stopPropagation(); openDeleteConfirm(folder); }}
                          title="Delete"
                          style={{ marginLeft: 'auto', width: 22, height: 22 }}
                        >
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                  {level.inputText.trim() && !levelFolders.some(f => f.folder_name.toLowerCase() === level.inputText.trim().toLowerCase()) && (
                    <div
                      className="mf-dropdown-item mf-create-option"
                      onClick={() => {
                        setFolderLevels(prev => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], showAutocomplete: false };
                          return next;
                        });
                      }}
                    >
                      <FiPlus size={16} />
                      + New {isRoot ? 'folder' : 'sub-folder'}: &ldquo;{level.inputText.trim()}&rdquo;
                    </div>
                  )}
                  {!level.inputText.trim() && levelFolders.length === 0 && (
                    <div className="mf-dropdown-item" style={{ color: 'var(--gray-400)', cursor: 'default' }}>
                      No {isRoot ? 'folders' : 'sub-folders'} yet. Type a name to create one.
                    </div>
                  )}
                </div>
              )}

              {idx === newLevelIndex && formErrors.folder_name && (
                <span className="mf-field-error">{formErrors.folder_name}</span>
              )}

              {/* Rename prompt — Edit existing or Add new? */}
              {renamePrompt && renamePrompt.levelIndex === idx && (
                <div className="mf-rename-prompt">
                  <div className="mf-rename-prompt-text">
                    <FiEdit2 size={14} />
                    <span>You changed &ldquo;{renamePrompt.oldName}&rdquo; to &ldquo;{renamePrompt.newName}&rdquo;</span>
                  </div>
                  <div className="mf-rename-prompt-actions">
                    <button
                      className="mf-btn-primary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        handleFormChange('folder_name', renamePrompt.newName);
                        setRenamePrompt(null);
                      }}
                    >
                      <FiEdit2 size={13} /> Edit &ldquo;{renamePrompt.oldName}&rdquo;
                    </button>
                    <button
                      className="mf-btn-secondary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        // Convert to a new folder creation: deselect this level and type the new name
                        setFolderLevels(prev => {
                          const next = [...prev];
                          next[idx] = { inputText: renamePrompt.newName, selectedFolder: null, showAutocomplete: false };
                          // Remove deeper levels
                          return next.slice(0, idx + 1);
                        });
                        setEditingFolder(idx > 0 ? folderLevels[idx - 1]?.selectedFolder || null : null);
                        setForm(prev => ({ ...prev, folder_name: '' }));
                        setRenamePrompt(null);
                      }}
                    >
                      <FiPlus size={13} /> Add New &ldquo;{renamePrompt.newName}&rdquo;
                    </button>
                    <button
                      className="mf-btn-secondary"
                      style={{ fontSize: 13, padding: '5px 14px' }}
                      onClick={() => {
                        // Revert name change
                        setFolderLevels(prev => {
                          const next = [...prev];
                          next[idx] = { ...next[idx], inputText: renamePrompt.oldName };
                          return next;
                        });
                        handleFormChange('folder_name', renamePrompt.oldName);
                        setRenamePrompt(null);
                      }}
                    >
                      <FiX size={13} /> Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Move tree picker */}
              {showMoveTree && level.selectedFolder && level.selectedFolder.folder_id === editingFolder?.folder_id && (
                <div className="mf-move-tree">
                  <div className="mf-move-tree-header">
                    <FiMove size={14} />
                    <span>Move &ldquo;{editingFolder.folder_name}&rdquo; to:</span>
                    <button className="mf-btn-close" onClick={() => { setShowMoveTree(false); setMoveTarget(null); }} style={{ marginLeft: 'auto' }}>
                      <FiX size={14} />
                    </button>
                  </div>
                  <div className="mf-move-tree-list">
                    <div
                      className={`mf-move-tree-item ${moveTarget?.id === null ? 'selected' : ''}`}
                      onClick={() => setMoveTarget({ id: null, name: 'Root level' })}
                    >
                      <FiFolder size={14} />
                      <span>Root level (top-level folder)</span>
                    </div>
                    {moveTargetTree.map(f => {
                      const depthOk = moveMaxDepthOk(Number(f.folder_id));
                      return (
                        <div
                          key={f.folder_id}
                          className={`mf-move-tree-item ${moveTarget?.id === Number(f.folder_id) ? 'selected' : ''} ${!depthOk ? 'disabled' : ''}`}
                          style={{ paddingLeft: 12 + Number(f.folder_depth) * 20 }}
                          onClick={() => depthOk && setMoveTarget({ id: Number(f.folder_id), name: f.folder_name })}
                          title={!depthOk ? `Cannot move here — would exceed ${MAX_SUBFOLDER_LEVELS + 1} levels` : f.folder_name}
                        >
                          <FiFolder size={14} style={f.folder_color ? { color: f.folder_color } : undefined} />
                          <span>{f.folder_name}</span>
                          {Number(f.umbrella_folder) === 1 && <span className="mf-umbrella-badge">&#9730;</span>}
                          {!depthOk && <span className="mf-move-depth-warn">too deep</span>}
                        </div>
                      );
                    })}
                  </div>
                  {formErrors.move && <span className="mf-field-error">{formErrors.move}</span>}
                  {moveTarget && (
                    <div className="mf-move-tree-actions">
                      <button className="mf-btn-primary" onClick={handleMoveFolder}>
                        <FiCheck size={16} /> Move to &ldquo;{moveTarget.name}&rdquo;
                      </button>
                      <button className="mf-btn-secondary" onClick={() => { setShowMoveTree(false); setMoveTarget(null); }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Breadcrumb path indicator */}
        {breadcrumbPath && folderLevels.some(l => l.selectedFolder || l.inputText.trim()) && (
          <div className="mf-level-breadcrumb">
            <FiChevronRight size={12} style={{ flexShrink: 0 }} />
            <span>{breadcrumbPath}</span>
          </div>
        )}

        {/* ---- Owner Chips (always visible) ---- */}
        <div className="mf-field-group" style={{ marginTop: 16 }}>
          <label className="mf-field-label">
            Owners
            {newLevelIndex > 0 && <span className="mf-required">*</span>}
            {isAccountOwner && !ownersLocked && <span className="mf-field-hint" style={{ display: 'inline', marginLeft: 8 }}>(you can add/remove owners)</span>}
          </label>
          {ownersLocked ? (
            /* Sub-folder under a user-owned parent: owner is inherited, not editable. */
            <>
              <div className="mf-owner-grid" style={{ marginTop: 4 }}>
                {(inheritedParentOwners || []).map(entity => (
                  <span key={`${entity.owner_type}-${entity.owner_id}`} className={`mf-owner-chip selected ${entity.nickname ? 'has-nick' : ''}`} style={{ cursor: 'default' }}>
                    <span className="mf-chip-text">{entity.display_name}{entity.nickname ? ` · ${entity.nickname}` : ''}</span>
                    {entity.nickname && <span className="mf-chip-nickonly">{entity.nickname}</span>}
                    <span className="mf-chip-badge">{entity.badge}</span>
                  </span>
                ))}
              </div>
              <p className="mf-field-hint" style={{ marginTop: 4 }}>
                <FiInfo size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                Inherited from the parent folder — a sub-folder has the same owner as its parent.
              </p>
            </>
          ) : (
            <div className="mf-owner-grid" style={{ marginTop: 4 }}>
              {ownerEntities.map(entity => {
                const isSelected = formOwners.some(
                  o => o.owner_type === entity.owner_type && o.owner_id === entity.owner_id
                );
                const disabled = !isAccountOwner && entity.owner_type !== 'family_member' && entity.owner_id !== currentUser.user_id;

                return (
                  <button
                    key={`${entity.owner_type}-${entity.owner_id}`}
                    className={`mf-owner-chip ${isSelected ? 'selected' : ''} ${entity.nickname ? 'has-nick' : ''}`}
                    onClick={() => {
                      if (disabled) return;
                      if (isSelected) {
                        removeFormOwner(entity);
                      } else {
                        addFormOwner(entity);
                      }
                    }}
                    title={disabled ? 'You can only manage your own folders' : entity.display_name}
                    style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                  >
                    <span className="mf-chip-text">{entity.display_name}{entity.nickname ? ` · ${entity.nickname}` : ''}</span>
                    {entity.nickname && <span className="mf-chip-nickonly">{entity.nickname}</span>}
                    <span className="mf-chip-badge">{entity.badge}</span>
                  </button>
                );
              })}
            </div>
          )}
          {ownerEntities.length === 0 && (
            <p className="mf-field-hint">No owner entities found. Please set up your profile first.</p>
          )}
          {formErrors.owners && <span className="mf-field-error">{formErrors.owners}</span>}
        </div>

        {/* Umbrella folder warning */}
        {isNewAtAnyLevel && newLevelIndex === 0 && formOwners.length === 0 && (
          <div className="mf-umbrella-hint">
            <FiInfo size={14} />
            <span>No owner selected — this will be created as an <strong>Umbrella Folder</strong>. Sub-folders can be added later. No documents can be uploaded directly to an umbrella folder.</span>
          </div>
        )}

        {/* ---- Description, Icon, Color (always visible) ---- */}
        <div className="mf-field-group">
          <label className="mf-field-label">Description</label>
          <textarea
            className="mf-input mf-textarea"
            value={form.folder_description}
            onChange={e => handleFormChange('folder_description', e.target.value)}
            placeholder="Optional description..."
          />
        </div>

        <div className="mf-field-group">
          <label className="mf-field-label">Icon</label>
          <div className="mf-icon-grid">
            {FOLDER_ICONS.map(icon => (
              <button
                key={icon.key}
                className={`mf-icon-option ${form.folder_icon === icon.key ? 'selected' : ''}`}
                onClick={() => handleFormChange('folder_icon', form.folder_icon === icon.key ? '' : icon.key)}
                title={icon.label}
                type="button"
              >
                {icon.emoji}
              </button>
            ))}
          </div>
        </div>

        <div className="mf-field-group">
          <label className="mf-field-label">Color Tag</label>
          <div className="mf-color-grid">
            {FOLDER_COLORS.map(color => (
              <button
                key={color}
                className={`mf-color-option ${form.folder_color === color ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => handleFormChange('folder_color', form.folder_color === color ? '' : color)}
                title={color}
                type="button"
              />
            ))}
          </div>
        </div>

        {/* ---- Summary ---- */}
        {showSummary && (
          <div className="mf-summary">
            <div className="mf-summary-title"><FiCheck size={16} /> Review Before Saving</div>
            <div className="mf-summary-row">
              <span className="mf-summary-label">Action</span>
              <span className="mf-summary-value">
                {isNewAtAnyLevel ? 'Create New Folder' : nameChanged ? `Rename "${editingFolder?.folder_name}" → "${form.folder_name.trim()}"` : 'Edit Folder'}
              </span>
            </div>
            {/* Show folder hierarchy by level */}
            {folderLevels[0]?.selectedFolder && (
              <div className="mf-summary-row">
                <span className="mf-summary-label">Parent folder</span>
                <span className="mf-summary-value">{folderLevels[0].inputText.trim()}</span>
              </div>
            )}
            {folderLevels[1] && (folderLevels[1].selectedFolder || folderLevels[1].inputText.trim()) && (
              <div className="mf-summary-row">
                <span className="mf-summary-label">Sub folder</span>
                <span className="mf-summary-value">
                  {folderLevels[1].selectedFolder
                    ? (folderLevels[1].selectedFolder.folder_id === editingFolder?.folder_id ? form.folder_name.trim() : folderLevels[1].inputText.trim())
                    : <>{folderLevels[1].inputText.trim()} <em style={{ color: '#1565c0' }}>(new)</em></>}
                </span>
              </div>
            )}
            {folderLevels[2] && (folderLevels[2].selectedFolder || folderLevels[2].inputText.trim()) && (
              <div className="mf-summary-row">
                <span className="mf-summary-label">Sub-sub folder</span>
                <span className="mf-summary-value">
                  {folderLevels[2].selectedFolder
                    ? (folderLevels[2].selectedFolder.folder_id === editingFolder?.folder_id ? form.folder_name.trim() : folderLevels[2].inputText.trim())
                    : <>{folderLevels[2].inputText.trim()} <em style={{ color: '#1565c0' }}>(new)</em></>}
                </span>
              </div>
            )}
            {folderLevels[3] && (folderLevels[3].selectedFolder || folderLevels[3].inputText.trim()) && (
              <div className="mf-summary-row">
                <span className="mf-summary-label">Sub-sub-sub folder</span>
                <span className="mf-summary-value">
                  {folderLevels[3].selectedFolder
                    ? (folderLevels[3].selectedFolder.folder_id === editingFolder?.folder_id ? form.folder_name.trim() : folderLevels[3].inputText.trim())
                    : <>{folderLevels[3].inputText.trim()} <em style={{ color: '#1565c0' }}>(new)</em></>}
                </span>
              </div>
            )}
            {/* Only show parent for new root folders (no selected parent) */}
            {isNewAtAnyLevel && newLevelIndex === 0 && (
              <div className="mf-summary-row">
                <span className="mf-summary-label">Folder name</span>
                <span className="mf-summary-value">{folderLevels[0].inputText.trim()} <em style={{ color: '#1565c0' }}>(new)</em></span>
              </div>
            )}
            <div className="mf-summary-row">
              <span className="mf-summary-label">Owner(s)</span>
              <span className="mf-summary-value">
                {formOwners.length > 0
                  ? formOwners.map(o => o.display_name).join(', ')
                  : <em style={{ color: '#1565c0' }}>Umbrella Folder (no owners — sub-folders only)</em>}
              </span>
            </div>
          </div>
        )}

        {/* ---- Action buttons (always visible) ---- */}
        <div className="mf-actions">
          {!showSummary ? (
            <button className="mf-btn-primary" onClick={handleReview} disabled={!canReview}
              style={!canReview ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
            >
              <FiCheck size={16} /> Review
            </button>
          ) : (
            <button className="mf-btn-primary" onClick={handleSave}>
              <FiCheck size={16} /> {isNewAtAnyLevel ? 'Confirm & Create' : 'Save Changes'}
            </button>
          )}
          <button className="mf-btn-secondary" onClick={closeForm}>Clear</button>
          {isEditing && editingFolder && (
            <button className="mf-btn-danger" onClick={() => openDeleteConfirm(editingFolder)} style={{ marginLeft: 'auto' }}>
              <FiTrash2 size={16} /> Delete
            </button>
          )}
        </div>
      </div>

      {/* ---- Delete Confirmation ---- */}
      {mode === 'delete' && editingFolder && (
        <div className="mf-form-card">
          <div className="mf-form-header">
            <div className="mf-form-title" style={{ color: '#c62828' }}>
              <FiAlertCircle size={18} /> Delete Folder
            </div>
            <button className="mf-btn-close" onClick={closeForm}><FiX size={18} /></button>
          </div>

          <div className="mf-delete-warning">
            <div className="mf-delete-warning-title">
              <FiAlertCircle size={16} /> Are you sure you want to delete &ldquo;{editingFolder.folder_name}&rdquo;?
            </div>
            {deleteChildCount > 0 && (
              <p>This folder has <strong>{deleteChildCount}</strong> sub-folder{deleteChildCount !== 1 ? 's' : ''} that will also be deleted.</p>
            )}
            {deleteDocCount > 0 && (
              (!isSharedFolder || deleteAction === 'all') ? (
                <p className="mf-delete-block">
                  <FiAlertCircle size={14} />
                  <span>This folder {deleteChildCount > 0 ? 'tree ' : ''}still holds <strong>{deleteDocCount}</strong> document{deleteDocCount !== 1 ? 's' : ''}. Move or delete {deleteDocCount !== 1 ? 'them' : 'it'} in Process Docs / Receipts first — a folder with documents can&rsquo;t be deleted.</span>
                </p>
              ) : (
                <p>There {deleteDocCount === 1 ? 'is' : 'are'} <strong>{deleteDocCount}</strong> document{deleteDocCount !== 1 ? 's' : ''} in this folder tree (kept — only your access is removed).</p>
              )
            )}

            {isSharedFolder && (
              <div className="mf-shared-warning">
                <div className="mf-shared-warning-title">
                  <FiInfo size={14} /> This is a shared folder with {deleteFolderOwners.length} owners:
                </div>
                <div className="mf-shared-owner-list">
                  {deleteFolderOwnerNames.map((name, i) => (
                    <span key={i} className="mf-shared-owner-chip">{name}</span>
                  ))}
                </div>

                {isAccountOwner ? (
                  <div className="mf-delete-options">
                    <label className="mf-radio-option">
                      <input
                        type="radio"
                        name="deleteAction"
                        checked={deleteAction === 'all'}
                        onChange={() => setDeleteAction('all')}
                      />
                      <span>Delete for all owners — removes the folder everywhere</span>
                    </label>
                    <label className="mf-radio-option">
                      <input
                        type="radio"
                        name="deleteAction"
                        checked={deleteAction === 'self'}
                        onChange={() => setDeleteAction('self')}
                      />
                      <span>Remove only my access — keep the folder for other owners</span>
                    </label>
                  </div>
                ) : (
                  <p className="mf-shared-note">
                    This will only remove <strong>your</strong> access. The folder will remain active for the other owner{deleteFolderOwners.length > 2 ? 's' : ''}.
                  </p>
                )}
              </div>
            )}

            {(!isSharedFolder || deleteAction === 'all') && deleteDocCount === 0 && (
              <p>This action will soft-delete the folder. It can be recovered from Data &amp; Backups.</p>
            )}
          </div>

          <div className="mf-actions" style={{ marginTop: 16 }}>
            {(() => {
              const blockedByDocs = (!isSharedFolder || deleteAction === 'all') && deleteDocCount > 0;
              return (
                <button
                  className="mf-btn-danger"
                  onClick={handleDelete}
                  disabled={blockedByDocs}
                  title={blockedByDocs ? 'Empty the folder of its documents first' : undefined}
                >
                  <FiTrash2 size={16} /> {isSharedFolder && deleteAction === 'self' ? 'Remove My Access' : 'Yes, Delete'}
                </button>
              );
            })()}
            <button className="mf-btn-secondary" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// Wrapper with sub-tabs: Owner Folders vs Client Folders
// In pillar mode (viewMode='clients', used by Client's Accounts → Manage Folders)
// it locks to the client view: the "Manage Your Folders" tab is hidden and the
// passed-in client is pre-selected.
// =============================================
interface ManageFoldersWrapperProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
}
const ManageFoldersWrapper: React.FC<ManageFoldersWrapperProps> = ({ viewMode = 'owner', selectedClientId: clientIdProp = null }) => {
  const lockToClients = viewMode === 'clients';
  // Single-mode now (no sub-tabs): owner folders in DMS, client folders in Client's Accounts.
  const subTab: 'owner' | 'clients' = lockToClients ? 'clients' : 'owner';
  const [selectedClientId, setSelectedClientId] = useState<number | string | null>(lockToClients ? clientIdProp : null);
  const [allUsers, setAllUsers] = useState<Record<string, any>[]>(() => getTableData('users'));

  useEffect(() => {
    loadTable('users').then(setAllUsers).catch(() => {});
  }, []);

  // In pillar mode, follow the client selected upstream.
  useEffect(() => {
    if (lockToClients) setSelectedClientId(clientIdProp);
  }, [lockToClients, clientIdProp]);

  const accountOwnerId = useMemo(() => {
    const owner = allUsers.find((u: any) => u.owner_id == null);
    return owner ? Number(owner.user_id) : 0;
  }, [allUsers]);

  const personalClients = useMemo(() => {
    if (!accountOwnerId) return [];
    // eslint-disable-next-line eqeqeq
    return allUsers.filter((u: any) => u.owner_id == accountOwnerId && u.is_deleted != 1 && u.relationship === 'Personal Client');
  }, [accountOwnerId, allUsers]);

  const selectedClient = useMemo(() => {
    if (!selectedClientId) return null;
    // eslint-disable-next-line eqeqeq
    return personalClients.find(c => c.user_id == selectedClientId) || null;
  }, [selectedClientId, personalClients]);

  return (
    <div>
      {/* Sub-tabs removed: DMS manages owner folders, Client's Accounts manages
          client folders — each consumer is single-mode (set via viewMode). */}
      {subTab === 'clients' && !selectedClientId && (
        <div className="mf-client-selector">
          <div className="mf-client-selector-header">
            <FiUsers size={20} />
            <h4>Select a Client</h4>
            <p>Choose a personal client to manage their folders. Each client's documents are kept separate.</p>
          </div>
          {personalClients.length === 0 ? (
            <div className="mf-client-selector-empty">
              <p>No personal clients found. Add clients in <strong>Family &amp; Businesses &gt; Your Clients</strong> first.</p>
            </div>
          ) : (
            <div className="mf-client-selector-list">
              {personalClients.map(c => (
                <button key={c.user_id} className="mf-client-selector-card" onClick={() => setSelectedClientId(c.user_id)}>
                  <div className="mf-client-selector-avatar">
                    {(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}
                  </div>
                  <div className="mf-client-selector-info">
                    <span className="mf-client-selector-name">{c.first_name} {c.last_name}</span>
                    {c.address_city && <span className="mf-client-selector-loc">{c.address_city}{c.address_state ? `, ${c.address_state}` : ''}</span>}
                  </div>
                  <FiChevronRight size={18} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {subTab === 'clients' && selectedClientId && (
        <div className="mf-client-selected-bar">
          <span>Managing folders for: <strong>{selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client'}</strong></span>
          <button className="mf-btn-secondary" onClick={() => setSelectedClientId(null)}>
            <FiX size={14} /> Change Client
          </button>
        </div>
      )}

      {(subTab === 'owner' || selectedClientId) && (
        <ManageFoldersTab key={`${subTab}-${selectedClientId}`} viewMode={subTab} selectedClientId={selectedClientId} />
      )}
    </div>
  );
};

export { ManageFoldersTab };
export default ManageFoldersWrapper;
